import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MpiresLogo } from "@/components/Brand";
import { Plus, LogOut, Search, PackageCheck, Truck, X, ChevronRight, Loader2, Megaphone, Star, SlidersHorizontal, Download } from "@/components/icons";
import { roleLabel } from "@/lib/format";

const fmtDataBR = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const STATUS_SEQ = ["Agendado", "Em trânsito", "Chegou", "Conferido", "Liberado para o depósito", "Finalizado"];
const ENVIO_STATUS = ["Pendente", "Finalizado"];
const HORARIOS = ["Manhã", "Tarde"];
const EMPS = ["1", "2", "3", "4"];

const DIAS_SEMANA_PT = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira",
  "Sexta-feira", "Sábado", "Domingo"];
const diaDaSemana = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return DIAS_SEMANA_PT[(d.getDay() + 6) % 7];
};

function ParceiroAutocomplete({ tipo, value, onChange, testId }) {
  const [q, setQ] = useState(value?.nome || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setQ(value?.nome || ""); }, [value?.codigo]);
  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const path = tipo === "fornecedor" ? "/logistica/fornecedores" : "/logistica/transportadoras";
        const { data } = await api.get(path, { params: { q, limit: 30 } });
        setItems(data || []);
      } finally { setLoading(false); }
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, tipo]);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={tipo === "fornecedor" ? "Buscar fornecedor..." : "Buscar transportadora..."}
          data-testid={testId}
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-black/10 bg-white shadow-lg">
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Buscando...</div>}
          {!loading && items.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nada encontrado</div>}
          {items.map((it) => (
            <button
              key={it.codigo}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(it); setQ(it.nome); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
              data-testid={`${testId}-opt-${it.codigo}`}
            >
              <div className="font-medium text-navy leading-tight">{it.nome}</div>
              <div className="text-[11px] text-slate-500">Cód. {it.codigo}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RecebimentoForm({ initial, onSubmit, onClose, busy }) {
  const [f, setF] = useState(() => ({
    fornecedor_codigo: initial?.fornecedor_codigo || "",
    fornecedor_nome: initial?.fornecedor_nome || "",
    emp: initial?.emp || "",
    data: initial?.data || "",
    horario: initial?.horario || "Manhã",
    nota_fiscal: initial?.nota_fiscal || "",
    sku: initial?.sku || "",
    kg: initial?.kg ?? "",
    volumes: initial?.volumes ?? "",
    paletes: initial?.paletes ?? "",
    status: initial?.status || "Agendado",
    observacoes: initial?.observacoes || "",
    destacar_tv: !!initial?.destacar_tv,
  }));
  const dia = diaDaSemana(f.data);
  const submit = (e) => {
    e.preventDefault();
    if (!f.fornecedor_codigo) return toast.error("Selecione o fornecedor");
    if (!f.data) return toast.error("Informe a data");
    if (!f.emp) return toast.error("Selecione a empresa (EMP)");
    onSubmit({
      ...f,
      kg: Number(f.kg) || 0,
      volumes: Number(f.volumes) || 0,
      paletes: Number(f.paletes) || 0,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="recebimento-form">
      <div>
        <label className="text-xs font-medium text-slate-600">Fornecedor *</label>
        <ParceiroAutocomplete
          tipo="fornecedor"
          value={{ codigo: f.fornecedor_codigo, nome: f.fornecedor_nome }}
          onChange={(p) => setF((s) => ({ ...s, fornecedor_codigo: p.codigo, fornecedor_nome: p.nome }))}
          testId="rec-fornecedor"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Data *</label>
          <input type="date" value={f.data} onChange={(e) => setF((s) => ({ ...s, data: e.target.value }))}
            data-testid="rec-data" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Dia (auto)</label>
          <input value={dia} readOnly data-testid="rec-dia"
            className="w-full h-10 px-3 rounded-lg border border-input bg-slate-50 text-sm text-slate-600" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Horário</label>
        <div className="flex gap-2" data-testid="rec-horario">
          {HORARIOS.map((h) => (
            <button key={h} type="button" onClick={() => setF((s) => ({ ...s, horario: h }))}
              data-testid={`rec-horario-${h}`}
              className={`flex-1 h-10 rounded-lg border text-sm font-medium transition-colors ${f.horario === h ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input"}`}>
              {h}
            </button>
          ))}
        </div>
      </div>
      {/* LOTE L-1: EMP (empresa) — obrigatório. 1/2/3/4 nessa ordem. */}
      <div>
        <label className="text-xs font-medium text-slate-600">EMP *</label>
        <div className="flex gap-2" data-testid="rec-emp">
          {["1", "2", "3", "4"].map((e) => (
            <button key={e} type="button" onClick={() => setF((s) => ({ ...s, emp: e }))}
              data-testid={`rec-emp-${e}`}
              className={`flex-1 h-10 rounded-lg border text-sm font-medium transition-colors ${f.emp === e ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input"}`}>
              {e}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Nota fiscal</label>
          <input value={f.nota_fiscal} onChange={(e) => setF((s) => ({ ...s, nota_fiscal: e.target.value }))}
            data-testid="rec-nf" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">SKU</label>
          <input value={f.sku} onChange={(e) => setF((s) => ({ ...s, sku: e.target.value }))}
            data-testid="rec-sku" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">KG</label>
          <input type="number" step="0.01" value={f.kg} onChange={(e) => setF((s) => ({ ...s, kg: e.target.value }))}
            data-testid="rec-kg" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Volumes</label>
          <input type="number" value={f.volumes} onChange={(e) => setF((s) => ({ ...s, volumes: e.target.value }))}
            data-testid="rec-volumes" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Paletes</label>
          <input type="number" value={f.paletes} onChange={(e) => setF((s) => ({ ...s, paletes: e.target.value }))}
            data-testid="rec-paletes" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Status</label>
        <select
          value={f.status}
          onChange={(e) => setF((s) => ({ ...s, status: e.target.value, destacar_tv: e.target.value === "Finalizado" ? false : s.destacar_tv }))}
          data-testid="rec-status"
          className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm"
        >
          {STATUS_SEQ.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Observações</label>
        <textarea value={f.observacoes} onChange={(e) => setF((s) => ({ ...s, observacoes: e.target.value }))}
          data-testid="rec-obs" rows={2}
          className="w-full px-3 py-2 rounded-lg border border-input bg-white text-sm" />
      </div>
      <label
        className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-amber-50 border border-amber-200 ${f.status === "Finalizado" ? "opacity-50" : ""}`}
        data-testid="rec-destacar-wrap"
      >
        <input
          type="checkbox"
          checked={f.destacar_tv}
          disabled={f.status === "Finalizado"}
          onChange={(e) => setF((s) => ({ ...s, destacar_tv: e.target.checked }))}
          data-testid="rec-destacar"
          className="w-4 h-4 accent-amber-500"
        />
        <Star className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium text-amber-900">Destacar na TV {f.status === "Finalizado" ? "(indisponível em Finalizado)" : ""}</span>
      </label>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
        <button type="submit" disabled={busy} data-testid="rec-save"
          className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
          {busy ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function EnvioForm({ initial, onSubmit, onClose, busy }) {
  const [f, setF] = useState(() => ({
    transportadora_codigo: initial?.transportadora_codigo || "",
    transportadora_nome: initial?.transportadora_nome || "",
    data: initial?.data || "",
    emp: initial?.emp || "",
    observacao: initial?.observacao || "",
    quantidade_notas: initial?.quantidade_notas ?? "",
    destacar_tv: !!initial?.destacar_tv,
    // LOTE AJ-2 L3 — envios ganham campo status (Pendente/Finalizado).
    status: initial?.status || "Pendente",
  }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.transportadora_codigo) return toast.error("Selecione a transportadora");
    if (!f.data) return toast.error("Informe a data");
    if (!f.emp) return toast.error("Selecione a empresa (EMP)");
    onSubmit({ ...f, quantidade_notas: Number(f.quantidade_notas) || 0 });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="envio-form">
      <div>
        <label className="text-xs font-medium text-slate-600">Transportadora *</label>
        <ParceiroAutocomplete
          tipo="transportadora"
          value={{ codigo: f.transportadora_codigo, nome: f.transportadora_nome }}
          onChange={(p) => setF((s) => ({ ...s, transportadora_codigo: p.codigo, transportadora_nome: p.nome }))}
          testId="env-transportadora"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Data *</label>
          <input type="date" value={f.data} onChange={(e) => setF((s) => ({ ...s, data: e.target.value }))}
            data-testid="env-data" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Quantidade de notas</label>
          <input type="number" value={f.quantidade_notas} onChange={(e) => setF((s) => ({ ...s, quantidade_notas: e.target.value }))}
            data-testid="env-qtd" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
      </div>
      {/* LOTE AJ-1 item 7: EMP (empresa) — obrigatório no envio também. */}
      <div>
        <label className="text-xs font-medium text-slate-600">EMP *</label>
        <div className="flex gap-2" data-testid="env-emp">
          {["1", "2", "3", "4"].map((e) => (
            <button key={e} type="button" onClick={() => setF((s) => ({ ...s, emp: e }))}
              data-testid={`env-emp-${e}`}
              className={`flex-1 h-10 rounded-lg border text-sm font-medium transition-colors ${f.emp === e ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-input"}`}>
              {e}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">Observação</label>
        <textarea value={f.observacao} onChange={(e) => setF((s) => ({ ...s, observacao: e.target.value }))}
          data-testid="env-obs" rows={2}
          className="w-full px-3 py-2 rounded-lg border border-input bg-white text-sm" />
      </div>
      {/* LOTE AJ-2 L3 — status do envio (Pendente / Finalizado). */}
      <div>
        <label className="text-xs font-medium text-slate-600">Status</label>
        <select
          value={f.status}
          onChange={(e) => setF((s) => ({ ...s, status: e.target.value, destacar_tv: e.target.value === "Finalizado" ? false : s.destacar_tv }))}
          data-testid="env-status"
          className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm"
        >
          {ENVIO_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <label
        className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-amber-50 border border-amber-200 ${f.status === "Finalizado" ? "opacity-50" : ""}`}
        data-testid="env-destacar-wrap"
      >
        <input
          type="checkbox"
          checked={f.destacar_tv}
          disabled={f.status === "Finalizado"}
          onChange={(e) => setF((s) => ({ ...s, destacar_tv: e.target.checked }))}
          data-testid="env-destacar"
          className="w-4 h-4 accent-amber-500"
        />
        <Star className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium text-amber-900">Destacar na TV {f.status === "Finalizado" ? "(indisponível em Finalizado)" : ""}</span>
      </label>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
        <button type="submit" disabled={busy} data-testid="env-save"
          className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
          {busy ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function AvisoForm({ initial, onSubmit, onClose, busy }) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(() => ({
    mensagem: initial?.mensagem || "",
    valido_de: initial?.valido_de || hoje,
    valido_ate: initial?.valido_ate || hoje,
    ativo: initial ? !!initial.ativo : true,
  }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.mensagem.trim()) return toast.error("Digite a mensagem");
    if (!f.valido_de) return toast.error("Informe 'Válido de'");
    if (!f.valido_ate) return toast.error("Informe 'Válido até'");
    if (f.valido_ate < f.valido_de) return toast.error("'Válido até' não pode ser antes de 'Válido de'");
    onSubmit({ ...f, mensagem: f.mensagem.trim() });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="aviso-form">
      <div>
        <label className="text-xs font-medium text-slate-600">Mensagem *</label>
        <textarea value={f.mensagem} onChange={(e) => setF((s) => ({ ...s, mensagem: e.target.value }))}
          data-testid="aviso-mensagem" rows={3} maxLength={280} autoFocus
          className="w-full px-3 py-2 rounded-lg border border-input bg-white text-sm" />
        <div className="text-[11px] text-slate-400 text-right">{f.mensagem.length}/280</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Válido de *</label>
          <input type="date" value={f.valido_de} onChange={(e) => setF((s) => ({ ...s, valido_de: e.target.value }))}
            data-testid="aviso-de" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Válido até *</label>
          <input type="date" value={f.valido_ate} min={f.valido_de} onChange={(e) => setF((s) => ({ ...s, valido_ate: e.target.value }))}
            data-testid="aviso-ate" className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm" />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg bg-slate-50 border border-slate-200">
        <input type="checkbox" checked={f.ativo} onChange={(e) => setF((s) => ({ ...s, ativo: e.target.checked }))}
          data-testid="aviso-ativo" className="w-4 h-4" />
        <span className="text-sm font-medium">Ativo (aparece na TV se dentro da validade)</span>
      </label>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
        <button type="submit" disabled={busy} data-testid="aviso-save"
          className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">
          {busy ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function StatusPill({ status }) {
  const idx = STATUS_SEQ.indexOf(status);
  const done = idx >= 0;
  const isFinal = status === "Liberado para o depósito";
  const cls = isFinal ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : done ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{status}</span>;
}

export default function Logistica() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("recebimentos");
  const [recs, setRecs] = useState([]);
  const [envs, setEnvs] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicateSeed, setDuplicateSeed] = useState(null);
  // LOTE AJ-2 L1 — estado dos filtros (abas separadas) + abertura do painel.
  const emptyRecFilters = {
    q: "", fornecedor_codigo: "", emp: "", data_de: "", data_ate: "",
    dia: "", horario: "", nota_fiscal: "", sku: "", status: "", destacar_tv: "",
  };
  const emptyEnvFilters = {
    q: "", transportadora_codigo: "", emp: "", data_de: "", data_ate: "",
    dia: "", status: "", destacar_tv: "",
  };
  const [recFilters, setRecFilters] = useState(emptyRecFilters);
  const [envFilters, setEnvFilters] = useState(emptyEnvFilters);
  const [showFilters, setShowFilters] = useState(false);

  const buildParams = (f) => {
    const o = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) {
        o[k] = k === "destacar_tv" ? v === "sim" : v;
      }
    });
    return o;
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [r, e, a] = await Promise.all([
        api.get("/logistica/recebimentos", { params: buildParams(recFilters) }),
        api.get("/logistica/envios", { params: buildParams(envFilters) }),
        api.get("/logistica/avisos"),
      ]);
      setRecs(r.data || []);
      setEnvs(e.data || []);
      setAvisos(a.data || []);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };
  // LOTE AJ-2 L1 — recarrega quando os filtros mudam.
  useEffect(() => { reload(); }, [JSON.stringify(recFilters), JSON.stringify(envFilters)]);

  // LOTE AJ-2 L2 — exporta XLSX da aba corrente, respeitando os filtros.
  const exportar = async () => {
    const isRec = tab === "recebimentos";
    const path = isRec ? "/logistica/recebimentos/export" : "/logistica/envios/export";
    const params = buildParams(isRec ? recFilters : envFilters);
    try {
      const resp = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const base = isRec ? "recebimentos" : "envios";
      a.download = `${base}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const advanceStatus = async (rec) => {
    const i = STATUS_SEQ.indexOf(rec.status);
    if (i < 0 || i >= STATUS_SEQ.length - 1) return;
    const next = STATUS_SEQ[i + 1];
    try {
      await api.patch(`/logistica/recebimentos/${rec.id}`, { status: next });
      toast.success(`Status: ${next}`);
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitRec = async (payload) => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/logistica/recebimentos/${editing.id}`, payload);
      else await api.post("/logistica/recebimentos", payload);
      toast.success("Recebimento salvo");
      setShowForm(false); setEditing(null); reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const submitEnv = async (payload) => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/logistica/envios/${editing.id}`, payload);
      else await api.post("/logistica/envios", payload);
      toast.success("Envio salvo");
      setShowForm(false); setEditing(null); reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  // Duplicar: abre o form como NOVO, pré-preenchido com um subset dos dados
  // do registro escolhido. Campos que sempre mudam (Data, Nota fiscal) ficam
  // em branco para forçar o usuário a preencher.
  const duplicateRec = (r) => {
    setEditing(null);
    setTab("recebimentos");
    setShowForm(true);
    // O RecebimentoForm lê `initial` no primeiro render — usamos um seed
    // temporário via state auxiliar.
    setDuplicateSeed({
      fornecedor_codigo: r.fornecedor_codigo,
      fornecedor_nome: r.fornecedor_nome,
      emp: r.emp || "",
      horario: r.horario,
      sku: r.sku,
      kg: r.kg,
      volumes: r.volumes,
      paletes: r.paletes,
      observacoes: r.observacoes,
      destacar_tv: !!r.destacar_tv,
      status: "Agendado",
      data: "",
      nota_fiscal: "",
    });
  };
  const duplicateEnv = (e) => {
    setEditing(null);
    setTab("envios");
    setShowForm(true);
    setDuplicateSeed({
      transportadora_codigo: e.transportadora_codigo,
      transportadora_nome: e.transportadora_nome,
      emp: e.emp || "",
      quantidade_notas: e.quantidade_notas,
      observacao: e.observacao,
      destacar_tv: !!e.destacar_tv,
      data: "",
    });
  };

  const removeRec = async (rec) => {
    if (!window.confirm("Excluir este recebimento?")) return;
    try { await api.delete(`/logistica/recebimentos/${rec.id}`); toast.success("Excluído"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const removeEnv = async (env) => {
    if (!window.confirm("Excluir este envio?")) return;
    try { await api.delete(`/logistica/envios/${env.id}`); toast.success("Excluído"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const submitAviso = async (payload) => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/logistica/avisos/${editing.id}`, payload);
      else await api.post("/logistica/avisos", payload);
      toast.success("Aviso salvo");
      setShowForm(false); setEditing(null); reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const toggleAviso = async (a) => {
    try { await api.patch(`/logistica/avisos/${a.id}`, { ativo: !a.ativo }); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const removeAviso = async (a) => {
    if (!window.confirm("Excluir este aviso?")) return;
    try { await api.delete(`/logistica/avisos/${a.id}`); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const avisoStatus = (a) => {
    const hoje = new Date().toISOString().slice(0, 10);
    if (!a.ativo) return { txt: "Inativo", cls: "bg-slate-200 text-slate-700" };
    if (a.valido_ate < hoje) return { txt: "Vencido", cls: "bg-red-100 text-red-700" };
    if (a.valido_de > hoje) return { txt: "Agendado", cls: "bg-blue-100 text-blue-700" };
    return { txt: "Na TV", cls: "bg-emerald-100 text-emerald-700" };
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-white border-b border-black/5 px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <MpiresLogo height={44} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-navy leading-tight">Módulo Logística</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.name} · {roleLabel(user?.role)}</div>
          </div>
        </div>
        <button onClick={() => { logout(); }} data-testid="logistica-logout"
          className="flex items-center gap-2 h-9 px-3 rounded-lg text-sm text-bad hover:bg-bad-soft">
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div role="tablist" className="inline-flex rounded-lg border border-black/10 bg-white p-0.5">
            <button role="tab" aria-selected={tab === "recebimentos"} onClick={() => setTab("recebimentos")}
              data-testid="tab-recebimentos"
              className={`h-9 px-3 rounded-md text-sm font-medium flex items-center gap-2 ${tab === "recebimentos" ? "bg-brand text-white" : "text-slate-600"}`}>
              <PackageCheck className="w-4 h-4" /> Recebimentos
              <span className="text-[11px] opacity-80">({recs.length})</span>
            </button>
            <button role="tab" aria-selected={tab === "envios"} onClick={() => setTab("envios")}
              data-testid="tab-envios"
              className={`h-9 px-3 rounded-md text-sm font-medium flex items-center gap-2 ${tab === "envios" ? "bg-brand text-white" : "text-slate-600"}`}>
              <Truck className="w-4 h-4" /> Envios
              <span className="text-[11px] opacity-80">({envs.length})</span>
            </button>
            <button role="tab" aria-selected={tab === "avisos"} onClick={() => setTab("avisos")}
              data-testid="tab-avisos"
              className={`h-9 px-3 rounded-md text-sm font-medium flex items-center gap-2 ${tab === "avisos" ? "bg-brand text-white" : "text-slate-600"}`}>
              <Megaphone className="w-4 h-4" /> Avisos
              <span className="text-[11px] opacity-80">({avisos.length})</span>
            </button>
          </div>
          <button onClick={() => { setEditing(null); setDuplicateSeed(null); setShowForm(true); }} data-testid="btn-novo"
            className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
            <Plus className="w-4 h-4" /> Novo {tab === "recebimentos" ? "recebimento" : tab === "envios" ? "envio" : "aviso"}
          </button>
        </div>

        {/* LOTE AJ-2 L1/L2 — barra de filtros + exportar + contagem (abas recebimentos/envios). */}
        {(tab === "recebimentos" || tab === "envios") && (
          <div className="mb-3 bg-white border border-black/10 rounded-xl">
            <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  data-testid="log-filtro-toggle"
                  className="flex items-center gap-2 h-9 px-3 rounded-lg border border-input text-sm text-navy hover:bg-slate-50"
                >
                  <SlidersHorizontal className="w-4 h-4" /> Filtros
                </button>
                <button
                  onClick={() => { tab === "recebimentos" ? setRecFilters(emptyRecFilters) : setEnvFilters(emptyEnvFilters); }}
                  data-testid="log-filtro-limpar"
                  className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
                >
                  Limpar filtros
                </button>
                <span className="text-xs text-slate-500" data-testid="log-contagem">
                  {(tab === "recebimentos" ? recs.length : envs.length)} registros
                </span>
              </div>
              <button
                onClick={exportar}
                data-testid="log-exportar"
                className="flex items-center gap-2 h-9 px-3 rounded-lg border border-input text-sm font-semibold text-navy hover:bg-slate-50"
              >
                <Download className="w-4 h-4" /> Exportar Excel
              </button>
            </div>
            {showFilters && tab === "recebimentos" && (
              <div className="border-t border-black/10 p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="log-filtro-rec-panel">
                <input placeholder="Busca livre" value={recFilters.q} onChange={(e) => setRecFilters((s) => ({ ...s, q: e.target.value }))}
                  data-testid="log-f-rec-q" className="h-9 px-2 rounded-lg border border-input text-sm col-span-2" />
                <select value={recFilters.emp} onChange={(e) => setRecFilters((s) => ({ ...s, emp: e.target.value }))}
                  data-testid="log-f-rec-emp" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">EMP (todas)</option>
                  {EMPS.map((e) => <option key={e} value={e}>EMP {e}</option>)}
                </select>
                <select value={recFilters.status} onChange={(e) => setRecFilters((s) => ({ ...s, status: e.target.value }))}
                  data-testid="log-f-rec-status" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Status (todos)</option>
                  {STATUS_SEQ.map((s) => <option key={s}>{s}</option>)}
                </select>
                <input type="date" value={recFilters.data_de} onChange={(e) => setRecFilters((s) => ({ ...s, data_de: e.target.value }))}
                  data-testid="log-f-rec-de" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <input type="date" value={recFilters.data_ate} onChange={(e) => setRecFilters((s) => ({ ...s, data_ate: e.target.value }))}
                  data-testid="log-f-rec-ate" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <select value={recFilters.horario} onChange={(e) => setRecFilters((s) => ({ ...s, horario: e.target.value }))}
                  data-testid="log-f-rec-horario" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Horário (todos)</option>
                  {HORARIOS.map((h) => <option key={h}>{h}</option>)}
                </select>
                <select value={recFilters.dia} onChange={(e) => setRecFilters((s) => ({ ...s, dia: e.target.value }))}
                  data-testid="log-f-rec-dia" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Dia (todos)</option>
                  {DIAS_SEMANA_PT.map((d) => <option key={d}>{d}</option>)}
                </select>
                <input placeholder="Nota Fiscal" value={recFilters.nota_fiscal} onChange={(e) => setRecFilters((s) => ({ ...s, nota_fiscal: e.target.value }))}
                  data-testid="log-f-rec-nf" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <input placeholder="SKU" value={recFilters.sku} onChange={(e) => setRecFilters((s) => ({ ...s, sku: e.target.value }))}
                  data-testid="log-f-rec-sku" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <select value={recFilters.destacar_tv} onChange={(e) => setRecFilters((s) => ({ ...s, destacar_tv: e.target.value }))}
                  data-testid="log-f-rec-tv" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Destaque TV</option>
                  <option value="sim">Em destaque</option>
                  <option value="nao">Sem destaque</option>
                </select>
              </div>
            )}
            {showFilters && tab === "envios" && (
              <div className="border-t border-black/10 p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="log-filtro-env-panel">
                <input placeholder="Busca livre" value={envFilters.q} onChange={(e) => setEnvFilters((s) => ({ ...s, q: e.target.value }))}
                  data-testid="log-f-env-q" className="h-9 px-2 rounded-lg border border-input text-sm col-span-2" />
                <select value={envFilters.emp} onChange={(e) => setEnvFilters((s) => ({ ...s, emp: e.target.value }))}
                  data-testid="log-f-env-emp" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">EMP (todas)</option>
                  {EMPS.map((e) => <option key={e} value={e}>EMP {e}</option>)}
                </select>
                <select value={envFilters.status} onChange={(e) => setEnvFilters((s) => ({ ...s, status: e.target.value }))}
                  data-testid="log-f-env-status" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Status (todos)</option>
                  {ENVIO_STATUS.map((s) => <option key={s}>{s}</option>)}
                </select>
                <input type="date" value={envFilters.data_de} onChange={(e) => setEnvFilters((s) => ({ ...s, data_de: e.target.value }))}
                  data-testid="log-f-env-de" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <input type="date" value={envFilters.data_ate} onChange={(e) => setEnvFilters((s) => ({ ...s, data_ate: e.target.value }))}
                  data-testid="log-f-env-ate" className="h-9 px-2 rounded-lg border border-input text-sm" />
                <select value={envFilters.dia} onChange={(e) => setEnvFilters((s) => ({ ...s, dia: e.target.value }))}
                  data-testid="log-f-env-dia" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Dia (todos)</option>
                  {DIAS_SEMANA_PT.map((d) => <option key={d}>{d}</option>)}
                </select>
                <select value={envFilters.destacar_tv} onChange={(e) => setEnvFilters((s) => ({ ...s, destacar_tv: e.target.value }))}
                  data-testid="log-f-env-tv" className="h-9 px-2 rounded-lg border border-input text-sm">
                  <option value="">Destaque TV</option>
                  <option value="sim">Em destaque</option>
                  <option value="nao">Sem destaque</option>
                </select>
              </div>
            )}
          </div>
        )}

        {loading && <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>}

        {!loading && tab === "recebimentos" && (
          <div className="space-y-2" data-testid="lista-recebimentos">
            {recs.length === 0 && <div className="text-center text-sm text-slate-400 py-10">Nenhum recebimento agendado</div>}
            {recs.map((r) => (
              <div key={r.id} className={`bg-card rounded-xl border p-4 flex items-start justify-between gap-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)] ${r.destacar_tv ? "border-amber-400 bg-amber-50/40" : "border-black/5"}`}
                data-testid={`rec-card-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-navy text-sm">{r.fornecedor_nome}</span>
                    <span className="text-[11px] text-slate-500">Cód. {r.fornecedor_codigo}</span>
                    <StatusPill status={r.status} />
                    {r.destacar_tv && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                        <Star className="w-3 h-3" /> Destaque
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    EMP {r.emp || "—"} · {fmtDataBR(r.data)} · {r.dia} · {r.horario} · NF {r.nota_fiscal || "—"} · SKU {r.sku || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.kg} kg · {r.volumes} volumes · {r.paletes} paletes
                  </div>
                  {r.observacoes && <div className="text-xs text-slate-500 mt-1 italic">{r.observacoes}</div>}
                </div>
                <div className="flex flex-col gap-1 items-end shrink-0">
                  {STATUS_SEQ.indexOf(r.status) < STATUS_SEQ.length - 1 && (
                    <button onClick={() => advanceStatus(r)} data-testid={`rec-advance-${r.id}`}
                      className="flex items-center gap-1 h-8 px-3 rounded-lg bg-brand-soft text-brand text-xs font-semibold hover:bg-brand-soft/70">
                      Avançar <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => { setEditing(r); setDuplicateSeed(null); setShowForm(true); }} data-testid={`rec-edit-${r.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-slate-600 hover:bg-slate-100">Editar</button>
                  <button onClick={() => duplicateRec(r)} data-testid={`rec-duplicate-${r.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 font-medium">Duplicar</button>
                  <button onClick={() => removeRec(r)} data-testid={`rec-del-${r.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-bad hover:bg-bad-soft">Excluir</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "envios" && (
          <div className="space-y-2" data-testid="lista-envios">
            {envs.length === 0 && <div className="text-center text-sm text-slate-400 py-10">Nenhum envio agendado</div>}
            {envs.map((e) => (
              <div key={e.id} className={`bg-card rounded-xl border p-4 flex items-start justify-between gap-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)] ${e.destacar_tv ? "border-amber-400 bg-amber-50/40" : "border-black/5"}`}
                data-testid={`env-card-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-navy text-sm">{e.transportadora_nome}</span>
                    <span className="text-[11px] text-slate-500">Cód. {e.transportadora_codigo}</span>
                    <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200" data-testid={`env-emp-badge-${e.id}`}>
                      EMP {e.emp || "—"}
                    </span>
                    {/* LOTE AJ-2 L3 — status do envio */}
                    <span
                      className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        e.status === "Finalizado"
                          ? "bg-slate-200 text-slate-600 border-slate-300"
                          : "bg-emerald-100 text-emerald-700 border-emerald-200"
                      }`}
                      data-testid={`env-status-${e.id}`}
                    >
                      {e.status || "Pendente"}
                    </span>
                    {e.destacar_tv && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                        <Star className="w-3 h-3" /> Destaque
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {fmtDataBR(e.data)} · {e.dia} · {e.quantidade_notas} nota{e.quantidade_notas === 1 ? "" : "s"}
                  </div>
                  {e.observacao && <div className="text-xs text-slate-500 mt-1 italic">{e.observacao}</div>}
                </div>
                <div className="flex flex-col gap-1 items-end shrink-0">
                  <button onClick={() => { setEditing(e); setDuplicateSeed(null); setShowForm(true); }} data-testid={`env-edit-${e.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-slate-600 hover:bg-slate-100">Editar</button>
                  <button onClick={() => duplicateEnv(e)} data-testid={`env-duplicate-${e.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 font-medium">Duplicar</button>
                  <button onClick={() => removeEnv(e)} data-testid={`env-del-${e.id}`}
                    className="h-8 px-3 rounded-lg text-xs text-bad hover:bg-bad-soft">Excluir</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "avisos" && (
          <div className="space-y-2" data-testid="lista-avisos">
            {avisos.length === 0 && <div className="text-center text-sm text-slate-400 py-10">Nenhum aviso cadastrado</div>}
            {avisos.map((a) => {
              const st = avisoStatus(a);
              return (
                <div key={a.id} className="bg-card rounded-xl border border-black/5 p-4 flex items-start justify-between gap-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)]"
                  data-testid={`aviso-card-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`aviso-status-${a.id}`}>{st.txt}</span>
                      <span className="text-xs text-slate-500">
                        {fmtDataBR(a.valido_de)} → {fmtDataBR(a.valido_ate)}
                      </span>
                    </div>
                    <div className="text-sm text-navy mt-1 break-words whitespace-pre-wrap">{a.mensagem}</div>
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    <button onClick={() => toggleAviso(a)} data-testid={`aviso-toggle-${a.id}`}
                      className={`h-8 px-3 rounded-lg text-xs font-semibold ${a.ativo ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"}`}>
                      {a.ativo ? "Desativar" : "Ativar"}
                    </button>
                    <button onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`aviso-edit-${a.id}`}
                      className="h-8 px-3 rounded-lg text-xs text-slate-600 hover:bg-slate-100">Editar</button>
                    <button onClick={() => removeAviso(a)} data-testid={`aviso-del-${a.id}`}
                      className="h-8 px-3 rounded-lg text-xs text-bad hover:bg-bad-soft">Excluir</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditing(null); setDuplicateSeed(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-navy">
                {editing ? "Editar" : (duplicateSeed ? "Duplicar" : "Novo")} {tab === "recebimentos" ? "recebimento" : tab === "envios" ? "envio" : "aviso"}
              </h2>
              <button onClick={() => { setShowForm(false); setEditing(null); setDuplicateSeed(null); }} className="p-1 hover:bg-slate-100 rounded" data-testid="form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {tab === "recebimentos" && (
              <RecebimentoForm initial={editing || duplicateSeed} busy={busy}
                onClose={() => { setShowForm(false); setEditing(null); setDuplicateSeed(null); }}
                onSubmit={submitRec} />
            )}
            {tab === "envios" && (
              <EnvioForm initial={editing || duplicateSeed} busy={busy}
                onClose={() => { setShowForm(false); setEditing(null); setDuplicateSeed(null); }}
                onSubmit={submitEnv} />
            )}
            {tab === "avisos" && (
              <AvisoForm initial={editing} busy={busy}
                onClose={() => { setShowForm(false); setEditing(null); setDuplicateSeed(null); }}
                onSubmit={submitAviso} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
